window.AAPlaybookService = (() => {
  const supabase = () => window.AASupabaseClient.getClient();

  const PLAYBOOK_STATUS_LABELS = {
  draft: 'Draft',
  submitted: 'Submitted',
  active_editing: 'Active Editing',
  locked: 'Locked',
  reopened: 'Reopened',
  archived: 'Archived'
};

  function formatPlaybookStatus(status) {
  return PLAYBOOK_STATUS_LABELS[status] || 'Draft';
}

// ===============================
// Sprint 12 — Phase 7
// Workflow Resolver Layer
// ===============================

function getPlaybookDeadline(playbook) {
  const month = Number(playbook?.month);
  const year = Number(playbook?.year);

  if (!month || !year) return null;

  /*
   * Playbooks close on the 15th of the month BEFORE
   * the marketing month.
   *
   * Examples:
   * September 2026 -> August 15, 2026
   * January 2027 -> December 15, 2026
   */
  return new Date(year, month - 2, 15, 23, 59, 59, 999);
}

function getCurrentProductionMonth(referenceDate = new Date()) {
  const year = referenceDate.getFullYear();
  const month = referenceDate.getMonth();

  /*
   * Through the 15th:
   * current Playbook = next calendar month.
   *
   * After the 15th:
   * current Playbook = two calendar months ahead.
   */
  const monthsAhead =
    referenceDate.getDate() <= 15 ? 1 : 2;

  const targetDate =
    new Date(year, month + monthsAhead, 1);

  return {
    month: targetDate.getMonth() + 1,
    year: targetDate.getFullYear()
  };
}

function isPastPlaybookDeadline(playbook) {
  const deadline = getPlaybookDeadline(playbook);

  if (!deadline) return false;

  return new Date() > deadline;
}

function isReopenWindowActive(playbook) {
  if (!playbook) return false;

  const expiresAt =
    playbook.reopenExpiresAt ||
    playbook.reopen_expires_at;

  if (expiresAt) {
    return new Date(expiresAt) > new Date();
  }

  return (
    playbook.reopenWindowActive === true ||
    playbook.reopen_window_active === true
  );
}

function resolvePlaybookStatus(playbook) {
  if (!playbook) return 'draft';

  const status = playbook.status;

  if (status === 'archived') return 'archived';

  if (status === 'reopened' && isReopenWindowActive(playbook)) {
    return 'reopened';
  }

  if (status === 'locked' || status === 'approved') {
    return 'locked';
  }

  if (isPastPlaybookDeadline(playbook)) {
    return 'locked';
  }

  if (status === 'submitted') {
    return 'submitted';
  }

  if (status === 'draft') {
    return 'draft';
  }

  return 'active_editing';
}

function applyDeadlineLock(playbook) {
  if (!playbook) return playbook;

  const resolvedStatus = resolvePlaybookStatus(playbook);

  if (resolvedStatus !== 'locked') {
    return playbook;
  }

  return {
    ...playbook,
    status: 'locked'
  };
}

function isCurrentPlaybook(playbook, context = {}) {
  if (!playbook) return false;

  if (resolvePlaybookStatus(playbook) === 'archived') return false;

  if (context.playbookId && playbook.id === context.playbookId) {
    return true;
  }

  if (
    context.clientId &&
    playbook.client_id &&
    context.clientId !== playbook.client_id
  ) {
    return false;
  }

  const playbookMonth = Number(playbook.month);
  const playbookYear = Number(playbook.year);

  const contextMonth = Number(context.month);
  const contextYear = Number(context.year);

  if (contextMonth && contextYear) {
    return playbookMonth === contextMonth && playbookYear === contextYear;
  }

  const now = new Date();

  return (
    playbookMonth === now.getMonth() + 1 &&
    playbookYear === now.getFullYear()
  );
}

function canEditPlaybook(playbook) {
  if (!playbook) {
    return false;
  }

  const resolvedStatus =
    resolvePlaybookStatus(playbook);

  return (
    resolvedStatus === 'draft' ||
    resolvedStatus === 'submitted' ||
    resolvedStatus === 'active_editing' ||
    resolvedStatus === 'reopened'
  );
}
function enterReopenedState(playbook) {
  if (!playbook) return null;

  const expiresAt = new Date();
  expiresAt.setHours(expiresAt.getHours() + 48);

  return {
    ...playbook,
    status: 'reopened',
    reopenWindowActive: true,
    reopenExpiresAt: expiresAt.toISOString()
  };
}

  // ===============================
  // Sprint 12 — Business Rules Layer
  // Phase 1: Read-only core functions
  // ===============================

  async function getPlaybookState(playbookId) {
    const db = supabase();

    if (!db || !playbookId) return null;

    const { data: playbook, error } = await db
      .from('aa_playbooks')
      .select('*')
      .eq('id', playbookId)
      .single();

    if (error || !playbook) return null;

    if (playbook.status === 'archived') return 'archived';
    if (playbook.status === 'reopened') return 'reopened';
    if (playbook.status === 'locked' || playbook.status === 'approved') return 'locked';

    const current = await getCurrentPlaybook(playbook.client_id);

    if (current && current.id === playbook.id) return 'current';

    return 'upcoming';
  }

  async function getCurrentPlaybook(clientId) {
  const db = supabase();

  if (!db || !clientId) return null;

  const target = getCurrentProductionMonth();

  const { data, error } = await db
    .from('aa_playbooks')
    .select('*')
    .eq('client_id', clientId)
    .eq('month', target.month)
    .eq('year', target.year)
    .neq('status', 'archived')
    .maybeSingle();

  if (error || !data) return null;

  return {
    id: data.id,
    month: data.month,
    year: data.year,
    status: data.status
  };
}

  async function getPlaybookWindow(clientId) {
    const db = supabase();

    if (!db || !clientId) {
      return {
        current: null,
        upcoming: [],
        history: []
      };
    }

    const { data, error } = await db
      .from('aa_playbooks')
      .select('*')
      .eq('client_id', clientId)
      .order('year', { ascending: true })
      .order('month', { ascending: true });

    if (error || !data?.length) {
      return {
        current: null,
        upcoming: [],
        history: []
      };
    }

    const current = await getCurrentPlaybook(clientId);
    const currentIndex = current
      ? data.findIndex((playbook) => playbook.id === current.id)
      : -1;

    const upcoming = currentIndex >= 0
      ? data.slice(currentIndex + 1, currentIndex + 6)
      : [];

    const upcomingIds = upcoming.map((playbook) => playbook.id);

    const history = data.filter((playbook) => {
      if (current && playbook.id === current.id) return false;
      if (upcomingIds.includes(playbook.id)) return false;
      return true;
    });

    return {
  current,
  nextPlaybook: upcoming[0] || null,
  upcoming,
  history
};
  }

  function getPlaybookLifecycleState(playbook) {
  return resolvePlaybookStatus(playbook);
}

function isPlaybookLocked(playbook) {
  return resolvePlaybookStatus(playbook) === 'locked';
}

function isPlaybookEditable(playbook, context = {}) {
  return canEditPlaybook(playbook, context);
}

  async function loadPlaybookById({ playbookId, clientId } = {}) {
    const db = supabase();
    if (!db || !playbookId || !clientId) return null;

    // Bind the requested Playbook to the selected Workspace client
    // before loading its fields, including for internal admin accounts.
    const { data: playbook, error: playbookError } = await db
      .from('aa_playbooks')
      .select('*')
      .eq('id', playbookId)
      .eq('client_id', clientId)
      .maybeSingle();

    if (playbookError || !playbook) return null;

    const { data: fields } = await db
      .from('aa_playbook_field_values')
      .select('*')
      .eq('playbook_id', playbook.id);

    const values = {};

    (fields || []).forEach((field) => {
      values[field.field_id] = field.field_value;
    });

    return { playbook, values };
  }

  async function getPlaybookDashboardById({ playbookId, clientId } = {}) {
    const db = supabase();
    if (!db || !playbookId || !clientId) return null;

    const { data: playbook, error } = await db
      .from('aa_playbooks')
      .select('*')
      .eq('id', playbookId)
      .eq('client_id', clientId)
      .maybeSingle();

    if (error || !playbook) return null;

    const { data: fields } = await db
      .from('aa_playbook_field_values')
      .select('updated_at')
      .eq('playbook_id', playbookId)
      .order('updated_at', { ascending: false })
      .limit(1);

    return {
      playbook,
      lastSavedAt: fields && fields.length ? fields[0].updated_at : null
    };
  }

  async function getPlaybookById({ playbookId }) {
    const db = supabase();
    if (!db || !playbookId) return null;

    const { data, error } = await db
      .from('aa_playbooks')
      .select('*')
      .eq('id', playbookId)
      .single();

    if (error) return null;

    return data;
  }

  async function getPlaybooksByClient({ clientId }) {
    const db = supabase();
    if (!db || !clientId) return [];

    const { data, error } = await db
      .from('aa_playbooks')
      .select(`
        id,
        month,
        year,
        status,
        created_at,
        updated_at
      `)
      .eq('client_id', clientId)
      .order('year', { ascending: false })
      .order('month', { ascending: false });

    if (error) {
      console.error('Error loading previous playbooks:', error);
      return [];
    }

    return data || [];
  }

  async function getClients() {
  const db = supabase();

  if (!db) return [];

  const { data, error } = await db
    .from('aa_clients')
    .select('*')
    .order('business_name', { ascending: true });

  if (error) {
    console.error('Error loading clients:', error);
    return [];
  }

  return data || [];
}

async function updateClient({
  id,
  businessName,
  clientCode,
  activeStatus,
  accountStatus,
  invoiceStatus
}) {
  const db = supabase();

  if (!db || !id) {
    return {
      success: false
    };
  }

  const { data, error } = await db
    .from('aa_clients')
    .update({
      business_name: businessName,
      ...(clientCode !== undefined
        ? {
            client_code:
              clientCode
                ? String(clientCode).trim().toUpperCase()
                : null
          }
        : {}),
      active_status: activeStatus,
      account_status: accountStatus,
      invoice_status: invoiceStatus,
      updated_at: new Date().toISOString()
    })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating client:', error);

    return {
      success: false,
      error
    };
  }

  return {
    success: true,
    client: data
  };
}

async function archiveClient({ id }) {
  const db = supabase();

  if (!db || !id) {
    return false;
  }

  const { error } = await db
    .from('aa_clients')
    .update({
      active_status: 'archived',
      updated_at: new Date().toISOString()
    })
    .eq('id', id);

  if (error) {
    console.error('Error archiving client:', error);
    return false;
  }

  return true;
}

async function createClient({
  businessName,
  clientCode
}) {
  const db = supabase();

  if (!db || !businessName?.trim()) {
    return {
      success: false,
      error: 'Business name is required.'
    };
  }

  const { data, error } = await db
    .from('aa_clients')
    .insert({
      business_name: businessName.trim(),
      ...(clientCode
        ? {
            client_code:
              String(clientCode)
                .trim()
                .toUpperCase()
          }
        : {})
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating client:', error);

    return {
      success: false,
      error
    };
  }

  return {
    success: true,
    client: data
  };
}

function getDefaultClientConfiguration() {
  return {
    relationships: [],
    services: [],
    serviceOptions: [],
    requiredAccessStatus: {}
  };
}

function normalizeClientConfiguration(configuration = {}) {
  return {
    ...getDefaultClientConfiguration(),
    ...configuration,
    relationships: Array.isArray(configuration.relationships) ? configuration.relationships : [],
    services: Array.isArray(configuration.services) ? configuration.services : [],
    serviceOptions: Array.isArray(configuration.serviceOptions) ? configuration.serviceOptions : [],
    requiredAccessStatus: configuration.requiredAccessStatus && typeof configuration.requiredAccessStatus === 'object'
      ? configuration.requiredAccessStatus
      : {}
  };
}

async function getClientConfiguration({ clientId }) {
  const db = supabase();

  if (!db || !clientId) {
    return getDefaultClientConfiguration();
  }

  const { data, error } = await db
    .from('aa_clients')
    .select('client_configuration')
    .eq('id', clientId)
    .single();

  if (error) {
    console.error('Error loading client configuration:', error);
    return getDefaultClientConfiguration();
  }

  return normalizeClientConfiguration(data?.client_configuration);
}

async function saveClientConfiguration({ clientId, configuration }) {
  const db = supabase();

  if (!db || !clientId) {
    return {
      success: false
    };
  }

  const cleanConfiguration = normalizeClientConfiguration(configuration);

  const { error } = await db
    .from('aa_clients')
    .update({
      client_configuration: cleanConfiguration,
      updated_at: new Date().toISOString()
    })
    .eq('id', clientId);

  if (error) {
    console.error('Error saving client configuration:', error);

    return {
      success: false,
      error
    };
  }

  return {
    success: true,
    configuration: cleanConfiguration
  };
}



async function getClientTemplateConfiguration({
  clientId,
  month,
  year
}) {
  const db = supabase();

  if (!db || !clientId) {
    return {
      assignment: null,
      template: null,
      sections: []
    };
  }

  let assignmentQuery =
    db
      .from('aa_client_template_assignments')
      .select(`
        id,
        client_id,
        template_id,
        assigned_month,
        assignment_status,
        assignment_config,
        updated_at
      `)
      .eq('client_id', clientId)
      .neq('assignment_status', 'archived');

  if (
    Number.isInteger(Number(month)) &&
    Number.isInteger(Number(year))
  ) {
    const effectiveDate =
      `${Number(year)}-${String(
        Number(month)
      ).padStart(2, '0')}-01`;

    assignmentQuery =
      assignmentQuery.lte(
        'assigned_month',
        effectiveDate
      );
  }

  const {
    data: assignments,
    error: assignmentError
  } =
    await assignmentQuery
      .order(
        'assigned_month',
        { ascending: false }
      )
      .order(
        'updated_at',
        { ascending: false }
      )
      .limit(1);

  if (assignmentError) {
    console.error(
      'Error loading client template assignment:',
      assignmentError
    );

    return {
      assignment: null,
      template: null,
      sections: []
    };
  }

  const assignment =
    assignments?.[0] || null;

  if (!assignment) {
    return {
      assignment: null,
      template: null,
      sections: []
    };
  }

  let sections =
    Array.isArray(
      assignment.assignment_config?.sections
    )
      ? assignment.assignment_config.sections
      : [];

  let template = null;

  if (assignment.template_id) {
    const {
      data: templateRow,
      error: templateError
    } =
      await db
        .from('aa_workspace_templates')
        .select(`
          id,
          template_name,
          template_type,
          template_config,
          template_revision,
          published_at,
          is_active,
          updated_at
        `)
        .eq(
          'id',
          assignment.template_id
        )
        .maybeSingle();

    if (templateError) {
      console.error(
        'Error loading assigned workspace template:',
        templateError
      );
    } else {
      template =
        templateRow || null;

      if (
        !sections.length &&
        Array.isArray(
          template
            ?.template_config
            ?.sections
        )
      ) {
        sections =
          template
            .template_config
            .sections;
      }
    }
  }

  return {
    assignment,
    template,
    sections
  };
}

  async function getClientAccount({ clientId }) {
    const db = supabase();

    if (!db || !clientId) {
      return null;
    }

    const { data, error } = await db
      .from('aa_clients')
      .select('*')
      .eq('id', clientId)
      .single();

    if (error) {
      return null;
    }

    return data;
  }

  async function getMarketingPlanByClient({ clientId }) {
    const db = supabase();

    if (!db || !clientId) {
      return {
        plan: null,
        sections: [],
        services: []
      };
    }

    const { data: plan, error: planError } = await db
      .from('aa_marketing_plans')
      .select('*')
      .eq('client_id', clientId)
      .order('last_updated', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (planError || !plan) {
      return {
        plan: null,
        sections: [],
        services: []
      };
    }

    const { data: sections, error: sectionsError } = await db
      .from('aa_marketing_plan_sections')
      .select('*')
      .eq('plan_id', plan.id)
      .order('sort_order', { ascending: true });

    if (sectionsError) {
      return {
        plan,
        sections: [],
        services: []
      };
    }

    const sectionIds =
      (sections || []).map(
        (section) => section.id
      );

    let services = [];

    if (sectionIds.length) {
      const {
        data: serviceRows,
        error: servicesError
      } = await db
        .from('aa_marketing_plan_services')
        .select('*')
        .in('section_id', sectionIds)
        .eq('active', true)
        .order('sort_order', { ascending: true });

      if (!servicesError) {
        services = serviceRows || [];
      }
    }

    return {
      plan: {
        id: plan.id,
        clientId: plan.client_id,
        name:
          plan.plan_name ||
          'Your Custom Marketing Plan',
        introText:
          plan.intro_text || '',
        status:
          plan.status || 'Active',
        lastUpdated:
          plan.last_updated || null
      },

      sections:
        (sections || []).map(
          (section) => ({
            id: section.id,
            planId: section.plan_id,
            title: section.title,
            sortOrder:
              section.sort_order,
            services:
              services.filter(
                (service) =>
                  service.section_id ===
                  section.id
              )
          })
        ),

      services
    };
  }

async function getServiceCatalog() {
  const db = supabase();

  if (!db) {
    return [];
  }

  const { data, error } = await db
    .from('aa_service_catalog')
    .select('*')
    .eq('is_active', true)
    .order('sort_order', { ascending: true })
    .order('service_name', { ascending: true });

  if (error) {
    console.error(
      'Error loading service catalog:',
      error
    );

    return [];
  }

  return (data || []).map((service) => ({
    id: service.id,
    name: service.service_name,
    description:
      service.service_description || '',
    category:
      service.category || '',
    defaultUnitLabel:
      service.default_unit_label ||
      'per month',
    sortOrder:
      Number(service.sort_order || 0),
    billingType:
      service.billing_type ||
      'included'
  }));
}

async function upsertServiceCatalogItem({
  serviceName,
  category,
  defaultUnitLabel,
  description
}) {
  const db = supabase();

  if (!db || !String(serviceName || '').trim()) {
    return {
      success: false,
      error: new Error('Service name is required.')
    };
  }

  const { data, error } = await db.rpc(
    'aa_upsert_service_catalog_item',
    {
      p_service_name: String(serviceName).trim(),
      p_category: String(category || '').trim() || null,
      p_default_unit_label:
        String(defaultUnitLabel || '').trim() || 'per month',
      p_service_description:
        String(description || '').trim() || null
    }
  );

  if (error) {
    console.error('Error saving service catalog item:', error);
    return { success: false, error };
  }

  return {
    success: data?.success === true,
    serviceId: data?.serviceId || null
  };
}

async function archiveServiceCatalogItem({ serviceId }) {
  const db = supabase();

  if (!db || !serviceId) {
    return {
      success: false,
      error: new Error('Service is required.')
    };
  }

  const { data, error } = await db.rpc(
    'aa_archive_service_catalog_item',
    { p_service_id: serviceId }
  );

  if (error) {
    console.error('Error archiving service catalog item:', error);
    return { success: false, error };
  }

  return {
    success: data?.success === true,
    serviceId: data?.serviceId || serviceId
  };
}

async function saveClientMarketingPackage({
  clientId,
  services,
  effectiveDate
}) {
  const db = supabase();

  if (!db || !clientId) {
    return {
      success: false
    };
  }

  const payload =
    Array.isArray(services)
      ? services.map((service, index) => ({
          serviceCatalogId:
            service.serviceCatalogId ||
            service.serviceId ||
            '',
          quantity:
            Math.max(
              Number(service.quantity || 0),
              0
            ),
          unitLabel:
            service.unitLabel ||
            service.unit ||
            'per month',
          notes:
            service.notes || '',
          customName:
            service.customName || '',
          displayOrder:
            Number(
              service.displayOrder ??
              index
            )
        }))
      : [];

  const { data, error } =
    await db.rpc(
      'aa_save_client_marketing_package_versioned',
      {
        p_client_id: clientId,
        p_services: payload,
        p_effective_date:
          effectiveDate || null
      }
    );

  if (error) {
    console.error(
      'Error saving client marketing package:',
      error
    );

    return {
      success: false,
      error
    };
  }

  return {
    success:
      data?.success !== false,
    effectiveDate:
      data?.effectiveDate || null,
    scheduled:
      data?.scheduled === true,
    result: data
  };
}

async function getClientMarketingPackage({
  clientId,
  month,
  year
}) {
  const db = supabase();

  if (!db || !clientId) {
    return {
      services: []
    };
  }

  const targetMonth =
    Number(month);

  const targetYear =
    Number(year);

  let targetDate;

  if (targetMonth && targetYear) {
    targetDate =
      `${targetYear}-${String(
        targetMonth
      ).padStart(2, '0')}-01`;
  } else {
    const now = new Date();

    targetDate = [
      now.getFullYear(),
      String(
        now.getMonth() + 1
      ).padStart(2, '0'),
      String(
        now.getDate()
      ).padStart(2, '0')
    ].join('-');
  }

  let packageQuery = db
    .from('aa_client_package_services')
    .select('*')
    .eq('client_id', clientId)
    .eq('is_active', true)
    .lte('effective_date', targetDate)
    .or(
      `expiration_date.is.null,expiration_date.gte.${targetDate}`
    )
    .order('display_order', {
      ascending: true
    })
    .order('effective_date', {
      ascending: false
    })
    .order('created_at', {
      ascending: false
    });

  const {
    data: rawPackageRows,
    error: packageError
  } = await packageQuery;

  if (
    packageError ||
    !rawPackageRows?.length
  ) {
    return {
      services: [],
      effectiveDate: null
    };
  }

  const seenServiceIds =
    new Set();

  const packageRows =
    rawPackageRows.filter(
      (row) => {
        const key =
          String(
            row.service_catalog_id
          );

        if (
          seenServiceIds.has(key)
        ) {
          return false;
        }

        seenServiceIds.add(key);
        return true;
      }
    );

  const serviceCatalogIds =
    packageRows
      .map(
        (row) =>
          row.service_catalog_id
      )
      .filter(Boolean);

  const {
    data: catalogRows,
    error: catalogError
  } = await db
    .from('aa_service_catalog')
    .select('*')
    .in('id', serviceCatalogIds);

  if (catalogError) {
    return {
      services: [],
      effectiveDate: null
    };
  }

  let progressRows = [];

  if (targetMonth && targetYear) {
    const packageServiceIds =
      packageRows.map(
        (row) => row.id
      );

    const {
      data: progressData,
      error: progressError
    } = await db
      .from('aa_client_package_progress')
      .select('*')
      .eq(
        'client_id',
        clientId
      )
      .eq(
        'month',
        targetMonth
      )
      .eq(
        'year',
        targetYear
      )
      .in(
        'package_service_id',
        packageServiceIds
      );

    if (!progressError) {
      progressRows =
        progressData || [];
    }
  }

  const effectiveDates =
    packageRows
      .map(
        (row) =>
          row.effective_date
      )
      .filter(Boolean)
      .sort();

  return {
    effectiveDate:
      effectiveDates.length
        ? effectiveDates[
            effectiveDates.length - 1
          ]
        : null,
    services:
      packageRows.map(
        (row) => {
          const catalogService =
            (catalogRows || []).find(
              (service) =>
                service.id ===
                row.service_catalog_id
            );

          const progress =
            progressRows.find(
              (item) =>
                item.package_service_id ===
                row.id
            );

          const included =
            Number(
              row.quantity || 0
            );

          const completed =
            Number(
              progress?.completed_quantity ||
              0
            );

          const remaining =
            Math.max(
              included -
              completed,
              0
            );

          return {
            id: row.id,
            serviceId:
              row.service_catalog_id,
            name:
              row.custom_name ||
              catalogService
                ?.service_name ||
              'Service',
            customName:
              row.custom_name || '',
            description:
              catalogService
                ?.service_description ||
              '',
            category:
              catalogService?.category ||
              '',
            included,
            completed,
            remaining,
            allowance:
              row.quantity,
            unit:
              row.unit_label ||
              catalogService
                ?.default_unit_label ||
              'included',
            notes:
              row.notes || '',
            status:
              row.status ||
              'active',
            effectiveDate:
              row.effective_date ||
              null,
            expirationDate:
              row.expiration_date ||
              null
          };
        }
      )
  };
}

async function getPackageChangeRequests({
  clientId
}) {
  const db = supabase();

  if (!db || !clientId) {
    return [];
  }

  const { data, error } = await db
    .from('aa_package_change_requests')
    .select('*')
    .eq('client_id', clientId)
    .order('requested_at', {
      ascending: false
    });

  if (error) {
    console.error(
      'Error loading package change requests:',
      error
    );

    return [];
  }

  return (data || []).map((item) => ({
    id: item.id,
    clientId: item.client_id,
    serviceName: item.service_name,
    currentPackageValue:
      item.current_package_value || '',
    requestedPackageValue:
      item.requested_package_value || '',
    clientNote:
      item.client_note || '',
    status:
      item.request_status || 'new',
    requestedAt:
      item.requested_at || '',
    reviewedAt:
      item.reviewed_at || '',
    updatedAt:
      item.updated_at || ''
  }));
}

async function submitPackageChangeRequest({
  clientId,
  serviceName,
  currentPackageValue,
  requestedPackageValue,
  clientNote
}) {
  const db = supabase();

  if (
    !db ||
    !clientId ||
    !serviceName ||
    !requestedPackageValue
  ) {
    return {
      success: false,
      error: new Error(
        'Service and requested change are required.'
      )
    };
  }

  const { data, error } =
    await db.rpc(
      'aa_submit_package_change_request',
      {
        p_client_id: clientId,
        p_service_name: serviceName,
        p_current_package_value:
          currentPackageValue || null,
        p_requested_package_value:
          requestedPackageValue,
        p_client_note:
          clientNote || null
      }
    );

  if (error) {
    console.error(
      'Error submitting package change request:',
      error
    );

    return {
      success: false,
      error
    };
  }

  return {
    success:
      data?.success === true,
    requestId:
      data?.requestId || null,
    status:
      data?.status || 'new'
  };
}

async function getAccessPlatformCatalog() {
  const db = supabase();

  if (!db) return [];

  const { data, error } = await db
    .from('aa_access_platforms')
    .select('*')
    .eq('is_active', true)
    .order('sort_order', { ascending: true })
    .order('platform_name', { ascending: true });

  if (error) {
    console.error(
      'Error loading access platform catalog:',
      error
    );
    return [];
  }

  return (data || []).map((platform) => ({
    id: platform.id,
    name: platform.platform_name,
    category: platform.category || '',
    defaultAccessMethod:
      platform.default_access_method || 'invite',
    sortOrder: Number(platform.sort_order || 0)
  }));
}

async function getClientAccessRequirements({ clientId }) {
  const db = supabase();

  if (!db || !clientId) {
    return [];
  }

  const { data, error } = await db
    .from('aa_client_access_requirements')
    .select('*')
    .eq('client_id', clientId)
    .eq('is_active', true)
    .order('display_order', { ascending: true })
    .order('created_at', { ascending: true });

  if (error) {
    console.error(
      'Error loading client access requirements:',
      error
    );
    return [];
  }

  return (data || []).map((item) => ({
    id: item.id,
    clientId: item.client_id,
    platformId: item.platform_id,
    platformName: item.platform_name,
    isRequired: item.is_required !== false,
    accessMethod: item.access_method || 'invite',
    loginEmail: item.login_email || '',
    loginUsername: item.login_username || '',
    clientInstructions: item.client_instructions || '',
    status: item.status || 'not_requested',
    requestedAt: item.requested_at || null,
    providedAt: item.provided_at || null,
    verifiedAt: item.verified_at || null,
    mfaStatus: item.mfa_status || 'unknown',
    mfaOwner: item.mfa_owner || '',
    internalNotes: item.internal_notes || '',
    vaultReference: item.vault_reference || '',
    displayOrder: Number(item.display_order || 0)
  }));
}

async function saveClientAccessRequirements({
  clientId,
  access
}) {
  const db = supabase();

  if (!db || !clientId) {
    return { success: false };
  }

  const payload =
    Array.isArray(access)
      ? access.map((item, index) => ({
          platformId: item.platformId || '',
          platformName: item.platformName || '',
          isRequired: item.isRequired !== false,
          accessMethod: item.accessMethod || 'invite',
          loginEmail: item.loginEmail || '',
          loginUsername: item.loginUsername || '',
          clientInstructions: item.clientInstructions || '',
          status: item.status || 'not_requested',
          requestedAt: item.requestedAt || '',
          providedAt: item.providedAt || '',
          verifiedAt: item.verifiedAt || '',
          mfaStatus: item.mfaStatus || 'unknown',
          mfaOwner: item.mfaOwner || '',
          internalNotes: item.internalNotes || '',
          vaultReference: item.vaultReference || '',
          displayOrder:
            Number(item.displayOrder ?? index)
        }))
      : [];

  const { data, error } = await db.rpc(
    'aa_save_client_access_requirements',
    {
      p_client_id: clientId,
      p_access: payload
    }
  );

  if (error) {
    console.error(
      'Error saving client access requirements:',
      error
    );
    return {
      success: false,
      error
    };
  }

  return {
    success: true,
    result: data
  };
}

async function markClientAccessProvided({ accessId }) {
  const db = supabase();

  if (!db || !accessId) {
    return { success: false };
  }

  const { data, error } = await db.rpc(
    'aa_client_mark_access_provided',
    {
      p_access_id: accessId
    }
  );

  if (error) {
    console.error(
      'Error marking access provided:',
      error
    );
    return {
      success: false,
      error
    };
  }

  return {
    success: true,
    result: data
  };
}

async function getAccessSubmission({
  targetType,
  targetId
}) {
  const db = supabase();

  if (!db || !targetType || !targetId) {
    return { success: false };
  }

  const { data, error } = await db.rpc(
    'aa_get_access_submission',
    {
      p_target_type: targetType,
      p_target_id: targetId
    }
  );

  if (error) {
    console.error(
      'Error loading access submission:',
      error
    );
    return { success: false, error };
  }

  return data || { success: true };
}

async function submitAccessDetails({
  targetType,
  targetId,
  loginUrl,
  loginEmail,
  loginUsername,
  password,
  mfaOwner,
  notes
}) {
  const db = supabase();

  if (!db || !targetType || !targetId) {
    return { success: false };
  }

  const { data, error } = await db.rpc(
    'aa_submit_access_details',
    {
      p_target_type: targetType,
      p_target_id: targetId,
      p_login_url: loginUrl || null,
      p_login_email: loginEmail || null,
      p_login_username: loginUsername || null,
      p_password: password || null,
      p_mfa_owner: mfaOwner || null,
      p_notes: notes || null
    }
  );

  if (error) {
    console.error(
      'Error submitting access details:',
      error
    );
    return { success: false, error };
  }

  return data || { success: true };
}

async function revealAccessPassword({
  submissionId
}) {
  const db = supabase();

  if (!db || !submissionId) {
    return { success: false };
  }

  const { data, error } = await db.rpc(
    'aa_reveal_access_password',
    {
      p_submission_id: submissionId
    }
  );

  if (error) {
    console.error(
      'Error revealing access password:',
      error
    );
    return { success: false, error };
  }

  return data || {
    success: true,
    password: ''
  };
}

async function setAccessSubmissionStatus({
  submissionId,
  status
}) {
  const db = supabase();

  if (!db || !submissionId || !status) {
    return { success: false };
  }

  const { data, error } = await db.rpc(
    'aa_set_access_submission_status',
    {
      p_submission_id: submissionId,
      p_status: status
    }
  );

  if (error) {
    console.error(
      'Error updating access submission status:',
      error
    );
    return { success: false, error };
  }

  return data || { success: true };
}

async function getClientContracts({
  clientId,
  includeInternal = false
}) {
  const db = supabase();

  if (!db || !clientId) {
    return [];
  }

  const { data, error } = await db
    .from('aa_contracts')
    .select(`
      id,
      client_id,
      contract_title,
      contract_type,
      version,
      status,
      effective_date,
      expiration_date,
      renewal_date,
      document_reference,
      client_visible,
      sent_for_signature_at,
      viewed_at,
      signed_at,
      signed_by_name,
      signed_by_email,
      archived_at,
      created_at,
      updated_at
    `)
    .eq('client_id', clientId)
    .is('archived_at', null)
    .order('effective_date', { ascending: false })
    .order('created_at', { ascending: false });

  if (error || !data) {
    if (error) {
      console.error('Unable to load contracts:', error);
    }
    return [];
  }

  const { data: documentRows, error: documentError } =
    await db
      .from('aa_contract_documents')
      .select(`
        id,
        contract_id,
        client_id,
        document_type,
        title,
        file_name,
        storage_path,
        is_client_visible,
        document_date,
        archived_at,
        created_at
      `)
      .eq('client_id', clientId)
      .is('archived_at', null)
      .order('created_at', { ascending: false });

  if (documentError) {
    console.error(
      'Unable to load contract documents:',
      documentError
    );
  }

  const hydratedDocuments =
    await Promise.all(
      (documentRows || []).map(async (document) => {
        let signedUrl = '';

        if (document.storage_path) {
          const { data: signedData } =
            await db.storage
              .from('contract-documents')
              .createSignedUrl(
                document.storage_path,
                60 * 60
              );

          signedUrl =
            signedData?.signedUrl || '';
        }

        return {
          id: document.id,
          contractId: document.contract_id,
          clientId: document.client_id,
          type: document.document_type,
          title: document.title,
          fileName: document.file_name,
          storagePath: document.storage_path,
          clientVisible:
            document.is_client_visible !== false,
          documentDate: document.document_date,
          createdAt: document.created_at,
          signedUrl
        };
      })
    );

  const documentsByContract =
    hydratedDocuments.reduce((map, document) => {
      if (!map.has(document.contractId)) {
        map.set(document.contractId, []);
      }

      map.get(document.contractId).push(document);
      return map;
    }, new Map());

  let internalNotesByContract = new Map();

  if (includeInternal && data.length) {
    const { data: internalRows, error: internalError } =
      await db
        .from('aa_contract_internal')
        .select('contract_id, internal_notes')
        .in(
          'contract_id',
          data.map((contract) => contract.id)
        );

    if (!internalError && internalRows) {
      internalNotesByContract =
        new Map(
          internalRows.map((row) => [
            row.contract_id,
            row.internal_notes || ''
          ])
        );
    }
  }

  return data.map((contract) => ({
    id: contract.id,
    clientId: contract.client_id,
    title: contract.contract_title,
    type: contract.contract_type,
    version: contract.version,
    status: contract.status,
    effectiveDate: contract.effective_date,
    expirationDate: contract.expiration_date,
    renewalDate: contract.renewal_date,
    documentReference: contract.document_reference,
    clientVisible:
      contract.client_visible !== false,
    sentForSignatureAt:
      contract.sent_for_signature_at,
    viewedAt: contract.viewed_at,
    signedAt: contract.signed_at,
    signedByName: contract.signed_by_name,
    signedByEmail: contract.signed_by_email,
    documents:
      documentsByContract.get(contract.id) || [],
    internalNotes:
      internalNotesByContract.get(contract.id) || '',
    createdAt: contract.created_at,
    updatedAt: contract.updated_at
  }));
}

async function createContract({
  clientId,
  title,
  type = 'Service Agreement',
  version = '',
  status = 'draft',
  effectiveDate = null,
  expirationDate = null,
  renewalDate = null,
  clientVisible = true,
  internalNotes = ''
}) {
  const db = supabase();

  if (!db || !clientId || !title) {
    return {
      success: false,
      error: new Error(
        'Client and contract title are required.'
      )
    };
  }

  const { data, error } = await db
    .from('aa_contracts')
    .insert({
      client_id: clientId,
      contract_title: title,
      contract_type:
        type || 'Service Agreement',
      version: version || null,
      status: status || 'draft',
      effective_date: effectiveDate || null,
      expiration_date: expirationDate || null,
      renewal_date: renewalDate || null,
      client_visible:
        clientVisible !== false,
      updated_at: new Date().toISOString()
    })
    .select('id')
    .single();

  if (error || !data?.id) {
    return {
      success: false,
      error:
        error ||
        new Error('Unable to create contract.')
    };
  }

  if (internalNotes?.trim()) {
    const { error: noteError } = await db
      .from('aa_contract_internal')
      .upsert({
        contract_id: data.id,
        internal_notes: internalNotes.trim(),
        updated_at: new Date().toISOString()
      });

    if (noteError) {
      console.error(
        'Contract created but internal notes were not saved:',
        noteError
      );
    }
  }

  return {
    success: true,
    contractId: data.id
  };
}

async function updateContract({
  contractId,
  title,
  type,
  version,
  status,
  effectiveDate,
  expirationDate,
  renewalDate,
  clientVisible,
  sentForSignatureAt,
  viewedAt,
  signedAt,
  signedByName,
  signedByEmail,
  internalNotes
}) {
  const db = supabase();

  if (!db || !contractId) {
    return {
      success: false,
      error: new Error('Contract is required.')
    };
  }

  const updates = {
    updated_at: new Date().toISOString()
  };

  if (title !== undefined) {
    updates.contract_title = title;
  }

  if (type !== undefined) {
    updates.contract_type = type;
  }

  if (version !== undefined) {
    updates.version = version || null;
  }

  if (status !== undefined) {
    updates.status = status;
  }

  if (effectiveDate !== undefined) {
    updates.effective_date =
      effectiveDate || null;
  }

  if (expirationDate !== undefined) {
    updates.expiration_date =
      expirationDate || null;
  }

  if (renewalDate !== undefined) {
    updates.renewal_date =
      renewalDate || null;
  }

  if (clientVisible !== undefined) {
    updates.client_visible =
      Boolean(clientVisible);
  }

  if (sentForSignatureAt !== undefined) {
    updates.sent_for_signature_at =
      sentForSignatureAt || null;
  }

  if (viewedAt !== undefined) {
    updates.viewed_at =
      viewedAt || null;
  }

  if (signedAt !== undefined) {
    updates.signed_at =
      signedAt || null;
  }

  if (signedByName !== undefined) {
    updates.signed_by_name =
      signedByName || null;
  }

  if (signedByEmail !== undefined) {
    updates.signed_by_email =
      signedByEmail || null;
  }

  const { error } = await db
    .from('aa_contracts')
    .update(updates)
    .eq('id', contractId);

  if (error) {
    return {
      success: false,
      error
    };
  }

  if (internalNotes !== undefined) {
    const { error: noteError } = await db
      .from('aa_contract_internal')
      .upsert({
        contract_id: contractId,
        internal_notes:
          String(internalNotes || '').trim(),
        updated_at: new Date().toISOString()
      });

    if (noteError) {
      return {
        success: false,
        error: noteError
      };
    }
  }

  return {
    success: true
  };
}

async function archiveContract({ contractId }) {
  const db = supabase();

  if (!db || !contractId) {
    return {
      success: false,
      error: new Error('Contract is required.')
    };
  }

  const now = new Date().toISOString();

  const { error } = await db
    .from('aa_contracts')
    .update({
      status: 'archived',
      archived_at: now,
      client_visible: false,
      updated_at: now
    })
    .eq('id', contractId);

  return {
    success: !error,
    error
  };
}

async function uploadContractDocument({
  clientId,
  contractId,
  file,
  documentType = 'agreement',
  title = '',
  clientVisible = true,
  documentDate = null
}) {
  const db = supabase();

  if (
    !db ||
    !clientId ||
    !contractId ||
    !file
  ) {
    return {
      success: false,
      error: new Error(
        'Client, contract, and PDF are required.'
      )
    };
  }

  const isPdf =
    file.type === 'application/pdf' ||
    String(file.name || '')
      .toLowerCase()
      .endsWith('.pdf');

  if (!isPdf) {
    return {
      success: false,
      error: new Error(
        'Contract documents must be PDF files.'
      )
    };
  }

  const safeFileName =
    String(file.name || 'contract.pdf')
      .replace(/[^a-zA-Z0-9._-]+/g, '-')
      .replace(/^-+|-+$/g, '') ||
    'contract.pdf';

  const storagePath =
    `${clientId}/${contractId}/${Date.now()}-${safeFileName}`;

  const { error: uploadError } =
    await db.storage
      .from('contract-documents')
      .upload(
        storagePath,
        file,
        {
          cacheControl: '3600',
          upsert: false,
          contentType: 'application/pdf'
        }
      );

  if (uploadError) {
    return {
      success: false,
      error: uploadError
    };
  }

  const { data: document, error: documentError } =
    await db
      .from('aa_contract_documents')
      .insert({
        contract_id: contractId,
        client_id: clientId,
        document_type:
          documentType || 'agreement',
        title:
          title ||
          file.name ||
          'Contract Document',
        file_name:
          file.name || safeFileName,
        storage_path: storagePath,
        is_client_visible:
          clientVisible !== false,
        document_date: documentDate || null
      })
      .select('id')
      .single();

  if (documentError || !document?.id) {
    await db.storage
      .from('contract-documents')
      .remove([storagePath]);

    return {
      success: false,
      error:
        documentError ||
        new Error(
          'Unable to save contract document.'
        )
    };
  }

  if (
    ['agreement', 'signed_agreement']
      .includes(documentType)
  ) {
    await db
      .from('aa_contracts')
      .update({
        document_reference: storagePath,
        updated_at: new Date().toISOString()
      })
      .eq('id', contractId);
  }

  return {
    success: true,
    documentId: document.id,
    storagePath
  };
}

async function archiveContractDocument({
  documentId
}) {
  const db = supabase();

  if (!db || !documentId) {
    return {
      success: false,
      error: new Error(
        'Contract document is required.'
      )
    };
  }

  const { error } = await db
    .from('aa_contract_documents')
    .update({
      archived_at: new Date().toISOString(),
      is_client_visible: false
    })
    .eq('id', documentId);

  return {
    success: !error,
    error
  };
}

async function deleteContractPermanently({
  contractId
}) {
  const db = supabase();

  if (!db || !contractId) {
    return {
      success: false,
      error: new Error(
        'Contract is required.'
      )
    };
  }

  const { data: contract, error: contractError } =
    await db
      .from('aa_contracts')
      .select('id, status, signed_at, signed_by_email')
      .eq('id', contractId)
      .single();

  if (contractError || !contract) {
    return {
      success: false,
      error:
        contractError ||
        new Error('Contract not found.')
    };
  }

  const { data: documents, error: documentsError } =
    await db
      .from('aa_contract_documents')
      .select('storage_path, document_type')
      .eq('contract_id', contractId);

  if (documentsError) {
    return {
      success: false,
      error: documentsError
    };
  }

  const hasSignatureRecord =
    Boolean(contract.signed_at) ||
    Boolean(contract.signed_by_email) ||
    (documents || []).some(
      (document) =>
        [
          'signed_agreement',
          'audit_certificate'
        ].includes(
          document.document_type
        )
    );

  const looksExecutedLegacy =
    [
      'active',
      'renewal_due',
      'expired'
    ].includes(contract.status) &&
    (documents || []).length > 0;

  if (
    hasSignatureRecord ||
    looksExecutedLegacy
  ) {
    return {
      success: false,
      error: new Error(
        'Signed or executed contracts should be archived instead of permanently deleted.'
      )
    };
  }

  const storagePaths =
    (documents || [])
      .map((document) => document.storage_path)
      .filter(Boolean);

  const { error: deleteError } =
    await db
      .from('aa_contracts')
      .delete()
      .eq('id', contractId);

  if (deleteError) {
    return {
      success: false,
      error: deleteError
    };
  }

  if (storagePaths.length) {
    const { error: storageError } =
      await db.storage
        .from('contract-documents')
        .remove(storagePaths);

    if (storageError) {
      console.warn(
        'Contract deleted but one or more PDF files could not be removed from storage:',
        storageError
      );
    }
  }

  return {
    success: true
  };
}

async function deleteContractDocumentPermanently({
  documentId
}) {
  const db = supabase();

  if (!db || !documentId) {
    return {
      success: false,
      error: new Error(
        'Contract document is required.'
      )
    };
  }

  const { data: document, error: documentError } =
    await db
      .from('aa_contract_documents')
      .select('id, contract_id, document_type, storage_path')
      .eq('id', documentId)
      .single();

  if (documentError || !document) {
    return {
      success: false,
      error:
        documentError ||
        new Error(
          'Contract document not found.'
        )
    };
  }

  if (
    [
      'signed_agreement',
      'audit_certificate'
    ].includes(document.document_type)
  ) {
    return {
      success: false,
      error: new Error(
        'Signed agreements and signature certificates should be archived instead of permanently deleted.'
      )
    };
  }

  const { error: deleteError } =
    await db
      .from('aa_contract_documents')
      .delete()
      .eq('id', documentId);

  if (deleteError) {
    return {
      success: false,
      error: deleteError
    };
  }

  if (document.storage_path) {
    await db
      .from('aa_contracts')
      .update({
        document_reference: null,
        updated_at: new Date().toISOString()
      })
      .eq('id', document.contract_id)
      .eq('document_reference', document.storage_path);

    const { error: storageError } =
      await db.storage
        .from('contract-documents')
        .remove([document.storage_path]);

    if (storageError) {
      console.warn(
        'Contract document record deleted but the PDF could not be removed from storage:',
        storageError
      );
    }
  }

  return {
    success: true
  };
}

async function invokeWaveService(body = {}) {
  const db = supabase();

  if (!db) {
    return {
      success: false,
      error: new Error(
        'Supabase client is unavailable.'
      )
    };
  }

  const { data, error } =
    await db.functions.invoke(
      'aa-wave-service',
      {
        body
      }
    );

  if (error) {
    return {
      success: false,
      error
    };
  }

  if (!data?.success) {
    return {
      success: false,
      error: new Error(
        data?.error ||
        'Unable to connect to Wave.'
      ),
      code: data?.code || ''
    };
  }

  return {
    success: true,
    data: data?.data || {}
  };
}

async function listWaveBusinesses() {
  const result =
    await invokeWaveService({
      action: 'list-businesses'
    });

  if (!result.success) {
    return result;
  }

  return {
    success: true,
    businesses:
      Array.isArray(
        result.data?.businesses
      )
        ? result.data.businesses
        : [],
    connection:
      result.data?.connection || null
  };
}

async function getWaveConnection() {
  const result =
    await invokeWaveService({
      action: 'get-connection'
    });

  if (!result.success) {
    return result;
  }

  return {
    success: true,
    connection:
      result.data?.connection || null
  };
}

async function setWaveBusiness({
  businessId
}) {
  const result =
    await invokeWaveService({
      action: 'set-business',
      businessId
    });

  if (!result.success) {
    return result;
  }

  return {
    success: true,
    connection:
      result.data?.connection || null
  };
}

async function listWaveCustomers() {
  const result =
    await invokeWaveService({
      action: 'list-customers'
    });

  if (!result.success) {
    return result;
  }

  return {
    success: true,
    customers:
      Array.isArray(result.data?.customers)
        ? result.data.customers
        : [],
    businessId:
      result.data?.businessId || '',
    businessName:
      result.data?.businessName || ''
  };
}

async function getClientWaveCustomer({
  clientId
}) {
  const result =
    await invokeWaveService({
      action: 'get-client-customer',
      clientId
    });

  if (!result.success) {
    return result;
  }

  return {
    success: true,
    mapping:
      result.data?.mapping || null
  };
}

async function setClientWaveCustomer({
  clientId,
  customerId
}) {
  const result =
    await invokeWaveService({
      action: 'set-client-customer',
      clientId,
      customerId
    });

  if (!result.success) {
    return result;
  }

  return {
    success: true,
    mapping:
      result.data?.mapping || null
  };
}

async function getClientWaveInvoices({
  clientId
}) {
  const result =
    await invokeWaveService({
      action: 'get-client-invoices',
      clientId
    });

  if (!result.success) {
    return result;
  }

  return {
    success: true,
    clientId:
      result.data?.clientId || clientId,
    waveCustomer:
      result.data?.waveCustomer || null,
    invoices:
      Array.isArray(result.data?.invoices)
        ? result.data.invoices
        : []
  };
}

async function getMyWaveInvoices({
  clientId
}) {
  const result =
    await invokeWaveService({
      action: 'get-my-invoices',
      clientId
    });

  if (!result.success) {
    return result;
  }

  return {
    success: true,
    clientId:
      result.data?.clientId || clientId,
    waveCustomer:
      result.data?.waveCustomer || null,
    invoices:
      Array.isArray(result.data?.invoices)
        ? result.data.invoices
        : []
  };
}

async function getClientBrandAssets({ clientId }) {
  const db = supabase();

  if (!db || !clientId) {
    return [];
  }

  const { data, error } = await db
    .from('aa_brand_assets')
    .select('*')
    .eq('client_id', clientId)
    .eq('is_active', true)
    .order('display_order', { ascending: true })
    .order('uploaded_at', { ascending: false });

  if (error || !data) {
    return [];
  }

  return Promise.all(
    data.map(async (asset) => {
      let signedUrl = '';

      if (asset.storage_path) {
        const { data: signed } =
          await db.storage
            .from('brand-assets')
            .createSignedUrl(
              asset.storage_path,
              3600
            );

        signedUrl =
          signed?.signedUrl || '';
      }

      return {
        id: asset.id,
        type: asset.asset_type,
        title: asset.asset_title,
        fileName: asset.file_name,
        storagePath: asset.storage_path,
        signedUrl,
        value: asset.asset_value || '',
        description: asset.description,
        uploadedAt: asset.uploaded_at,
        displayOrder: asset.display_order,
        createdAt: asset.created_at,
        updatedAt: asset.updated_at
      };
    })
  );
}

async function uploadBrandAssetFile({
  clientId,
  file
}) {
  const db = supabase();

  if (!db || !clientId || !file) {
    return {
      success: false
    };
  }

  const safeName =
    String(file.name || 'asset')
      .replace(/[^a-zA-Z0-9._-]+/g, '-');

  const path =
    `${clientId}/${Date.now()}-${safeName}`;

  const { error } =
    await db.storage
      .from('brand-assets')
      .upload(path, file, {
        upsert: false
      });

  if (error) {
    console.error(
      'Error uploading brand asset:',
      error
    );

    return {
      success: false,
      error
    };
  }

  return {
    success: true,
    path,
    fileName: file.name
  };
}

async function createBrandAsset({
  clientId,
  type,
  title,
  fileName,
  storagePath,
  value,
  description,
  displayOrder = 0
}) {
  const db = supabase();

  if (!db || !clientId || !type || !title) {
    return {
      success: false
    };
  }

  const user =
    (await db.auth.getUser())
      ?.data?.user || null;

  const { data, error } = await db
    .from('aa_brand_assets')
    .insert({
      client_id: clientId,
      asset_type: type,
      asset_title: title,
      file_name: fileName || null,
      storage_path: storagePath || null,
      asset_value: value || null,
      description: description || null,
      display_order: Number(displayOrder || 0),
      uploaded_by: user?.id || null,
      is_active: true,
      uploaded_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })
    .select()
    .single();

  if (error) {
    console.error(
      'Error creating brand asset:',
      error
    );

    return {
      success: false,
      error
    };
  }

  return {
    success: true,
    asset: data
  };
}

async function archiveBrandAsset({
  assetId
}) {
  const db = supabase();

  if (!db || !assetId) {
    return {
      success: false
    };
  }

  const { data, error } =
    await db.rpc(
      'aa_archive_brand_asset',
      {
        p_asset_id: assetId
      }
    );

  if (error) {
    console.error(
      'Error archiving brand asset:',
      error
    );

    return {
      success: false,
      error
    };
  }

  return {
    success:
      data?.success === true,
    result: data || null
  };
}

async function getVendorLibrary() {
  const db = supabase();

  if (!db) {
    return [];
  }

  const { data, error } = await db
    .from('aa_vendor_library')
    .select(
      'id, vendor_name, access_status, access_reference, internal_notes'
    )
    .eq('is_active', true)
    .order('vendor_name', {
      ascending: true
    });

  if (error) {
    console.error(
      'Error loading vendor library:',
      error
    );

    return [];
  }

  return (data || []).map((vendor) => ({
    id: vendor.id,
    vendorName:
      vendor.vendor_name || 'Vendor',
    accessStatus:
      vendor.access_status || 'unknown',
    accessReference:
      vendor.access_reference || '',
    internalNotes:
      vendor.internal_notes || ''
  }));
}

async function getClientVendorBrands({
  clientId,
  includeInternal = false
}) {
  const db = supabase();

  if (!db || !clientId) {
    return [];
  }

  if (!includeInternal) {
    const { data, error } =
      await db.rpc(
        'aa_get_client_vendor_access_details',
        {
          p_client_id: clientId
        }
      );

    if (error) {
      console.error(
        'Error loading client vendor access:',
        error
      );
      return [];
    }

    return (data || []).map((item) => ({
      relationshipId:
        item.relationship_id,
      vendorId:
        item.vendor_id,
      vendorName:
        item.vendor_name,
      accessStatus:
        item.access_status,
      submissionId:
        item.submission_id || null,
      hasPassword:
        item.has_password === true
    }));
  }

  const { data: relations, error: relationError } =
    await db
      .from('aa_client_vendor_brands')
      .select('*')
      .eq('client_id', clientId)
      .eq('is_active', true)
      .order('display_order', { ascending: true });

  if (relationError) {
    console.error(
      'Error loading client vendor relationships:',
      relationError
    );
    return [];
  }

  const vendorIds =
    (relations || [])
      .map((item) => item.vendor_id)
      .filter(Boolean);

  if (!vendorIds.length) {
    return [];
  }

  const { data: vendors, error: vendorError } =
    await db
      .from('aa_vendor_library')
      .select('*')
      .in('id', vendorIds);

  if (vendorError) {
    console.error(
      'Error loading vendor library:',
      vendorError
    );
    return [];
  }

  return (relations || []).map((relation) => {
    const vendor =
      (vendors || []).find(
        (item) =>
          item.id === relation.vendor_id
      );

    return {
      relationshipId: relation.id,
      vendorId: relation.vendor_id,
      vendorName:
        vendor?.vendor_name || 'Vendor',
      accessStatus:
        vendor?.access_status || 'unknown',
      accessReference:
        vendor?.access_reference || '',
      internalNotes:
        vendor?.internal_notes || '',
      displayOrder:
        relation.display_order || 0
    };
  });
}

async function saveClientVendorBrands({
  clientId,
  brands
}) {
  const db = supabase();

  if (!db || !clientId) {
    return {
      success: false
    };
  }

  const payload =
    Array.isArray(brands)
      ? brands.map((item, index) => ({
          vendorName:
            item.vendorName || '',
          accessStatus:
            item.accessStatus || '',
          displayOrder:
            Number(
              item.displayOrder ?? index
            )
        }))
      : [];

  const { data, error } = await db.rpc(
    'aa_save_client_vendor_brands',
    {
      p_client_id: clientId,
      p_brands: payload
    }
  );

  if (error) {
    console.error(
      'Error saving client vendor brands:',
      error
    );

    return {
      success: false,
      error
    };
  }

  return {
    success: true,
    result: data
  };
}


  async function saveField({ playbookId, sectionId, fieldId, value }) {
    const db = supabase();

    if (!db || !playbookId || !sectionId || !fieldId) {
      return false;
    }

    const { error } = await db
      .from('aa_playbook_field_values')
      .upsert(
        {
          playbook_id: playbookId,
          section_id: sectionId,
          field_id: fieldId,
          field_value: value,
          updated_at: new Date().toISOString()
        },
        { onConflict: 'playbook_id,section_id,field_id' }
      );

    if (error) {
      console.error('Error saving field:', error);
      return false;
    }

    return true;
  }

  async function saveSection({ playbookId, sectionId, values }) {
  const saves = Object.entries(values || {}).map(([fieldId, value]) => {
    return saveField({ playbookId, sectionId, fieldId, value });
  });

  const results = await Promise.all(saves);
  return results.every(Boolean);
}

async function saveSectionCompletion({
  playbookId,
  sectionId,
  completed
} = {}) {
  if (!playbookId || !sectionId) {
    return false;
  }

  return saveField({
    playbookId,
    sectionId,
    fieldId: `__section_complete__:${sectionId}`,
    value: completed === true
  });
}

async function savePlaybookSectionCount({
  playbookId,
  totalSections
} = {}) {
  if (!playbookId) {
    return false;
  }

  const count = Number(totalSections);

  if (!Number.isInteger(count) || count < 0) {
    return false;
  }

  return saveField({
    playbookId,
    sectionId: '__playbook_progress__',
    fieldId: '__total_sections__',
    value: count
  });
}

async function getPlaybookCompletion({
  playbookId
} = {}) {
  const db = supabase();

  if (!db || !playbookId) {
    return {
      completedSections: 0,
      totalSections: 0,
      percentage: 0
    };
  }

  const { data, error } = await db
    .from('aa_playbook_field_values')
    .select('section_id, field_id, field_value')
    .eq('playbook_id', playbookId);

  if (error) {
    console.error(
      'Error loading Playbook completion:',
      error
    );

    return {
      completedSections: 0,
      totalSections: 0,
      percentage: 0
    };
  }

  const rows = data || [];

  const totalRow = rows.find(
    (row) =>
      row.section_id === '__playbook_progress__' &&
      row.field_id === '__total_sections__'
  );

  const totalSections =
    Math.max(
      Number(totalRow?.field_value || 0),
      0
    );

  const completedSections = rows.filter(
    (row) =>
      String(row.field_id || '')
        .startsWith('__section_complete__:') &&
      (
        row.field_value === true ||
        row.field_value === 'true'
      )
  ).length;

  const percentage =
    totalSections > 0
      ? Math.min(
          Math.round(
            (completedSections / totalSections) * 100
          ),
          100
        )
      : 0;

  return {
    completedSections,
    totalSections,
    percentage
  };
}

async function savePlaybook({ playbookId, values }) {
    const sectionSaves = Object.entries(values || {}).map(([sectionId, sectionValues]) => {
      return saveSection({ playbookId, sectionId, values: sectionValues });
    });

    const results = await Promise.all(sectionSaves);
    return results.every(Boolean);
  }

  function getNextPlaybookMonth(playbook) {
    const currentMonth = Number(playbook?.month);
    const currentYear = Number(playbook?.year);

    if (!currentMonth || !currentYear) return null;

    if (currentMonth === 12) {
      return {
        month: 1,
        year: currentYear + 1
      };
    }

    return {
      month: currentMonth + 1,
      year: currentYear
    };
  }

  async function createPlaybook({ clientId, month, year }) {
    const db = supabase();

    if (!db || !clientId || !month || !year) {
      return {
        success: false,
        reason: 'missing_required_fields',
        playbook: null
      };
    }

    const { data: existingPlaybook, error: existingError } = await db
      .from('aa_playbooks')
      .select('*')
      .eq('client_id', clientId)
      .eq('month', month)
      .eq('year', year)
      .maybeSingle();

    if (existingError) {
      console.error('Error checking existing playbook:', existingError);

      

      return {
        success: false,
        reason: 'check_failed',
        playbook: null
      };
    }

    if (existingPlaybook) {
      return {
        success: true,
        reason: 'already_exists',
        playbook: existingPlaybook
      };
    }

    const { data: createdPlaybook, error: createError } = await db
      .from('aa_playbooks')
      .insert({
        client_id: clientId,
        month,
        year,
        status: 'draft'
      })
      .select('*')
      .single();

    if (createError) {
      console.error('Error creating playbook:', createError);

      return {
        success: false,
        reason: 'create_failed',
        playbook: null
      };
    }

    

    return {
      success: true,
      reason: 'created',
      playbook: createdPlaybook
    };
  }

async function ensurePlaybookWindow(clientId) {
  const db = supabase();

  if (!db || !clientId) {
    return [];
  }

  /*
   * Safety:
   * Only maintain a rolling window for clients that
   * already participate in the Playbook system.
   */
  const { data: existing, error: existingError } =
    await db
      .from('aa_playbooks')
      .select('id')
      .eq('client_id', clientId)
      .limit(1);

  if (existingError || !existing?.length) {
    return [];
  }

  const current = getCurrentProductionMonth();
  const results = [];

  for (let offset = 0; offset < 6; offset += 1) {
    const targetDate = new Date(
      current.year,
      current.month - 1 + offset,
      1
    );

    const result = await createPlaybook({
      clientId,
      month: targetDate.getMonth() + 1,
      year: targetDate.getFullYear()
    });

    results.push(result);
  }

  return results;
}

async function submitPlaybook(playbookId) {
  const db = supabase();

  if (!db || !playbookId) return false;

  const { error } = await db
    .from('aa_playbooks')
    .update({
      status: 'submitted',
      updated_at: new Date().toISOString()
    })
    .eq('id', playbookId);

  return !error;
}

async function lockPlaybook(playbookId) {
  const db = supabase();

  if (!db || !playbookId) return false;

  const { error } = await db
    .from('aa_playbooks')
    .update({
      status: 'locked',
      updated_at: new Date().toISOString()
    })
    .eq('id', playbookId);

  return !error;
}

async function reopenPlaybook(playbookId) {
  const db = supabase();

  if (!db || !playbookId) return false;

  const { data: playbook, error: loadError } = await db
    .from('aa_playbooks')
    .select('*')
    .eq('id', playbookId)
    .single();

  if (loadError || !playbook) return false;

  const reopenedPlaybook = enterReopenedState(playbook);

  const { error } = await db
    .from('aa_playbooks')
    .update({
      status: reopenedPlaybook.status,
      reopen_window_active: reopenedPlaybook.reopenWindowActive,
      reopen_expires_at: reopenedPlaybook.reopenExpiresAt,
      updated_at: new Date().toISOString()
    })
    .eq('id', playbookId);

  return !error;
}

async function requestPlaybookReopen({
  playbookId,
  feeAgreed
}) {
  const db = supabase();

  if (!db || !playbookId || feeAgreed !== true) {
    return {
      success: false,
      error: new Error(
        'The $49 reopen fee must be accepted before reopening.'
      )
    };
  }

  const { data, error } = await db.rpc(
    'aa_request_playbook_reopen',
    {
      p_playbook_id: playbookId,
      p_fee_agreed: true
    }
  );

  if (error) {
    console.error(
      'Error reopening Playbook:',
      error
    );

    return {
      success: false,
      error
    };
  }

  return {
    success: data?.success === true,
    alreadyReopened:
      data?.alreadyReopened === true,
    requestId:
      data?.requestId || null,
    expiresAt:
      data?.expiresAt || null,
    feeAmount:
      Number(data?.feeAmount || 49)
  };
}

async function archivePlaybook(playbookId) {
  const db = supabase();

  if (!db || !playbookId) return false;

  const { error } = await db
    .from('aa_playbooks')
    .update({
      status: 'archived',
      updated_at: new Date().toISOString()
    })
    .eq('id', playbookId);

  return !error;
}


function getLockReason(playbook) {
  if (!playbook) return null;

  const resolvedStatus = resolvePlaybookStatus(playbook);

  if (resolvedStatus === 'archived') {
    return 'archived';
  }

  if (resolvedStatus === 'locked') {
    return 'locked';
  }

  return null;
}

function isPlaybookReopened(playbook) {
  return resolvePlaybookStatus(playbook) === 'reopened';
}

function isPlaybookArchived(playbook) {
  return resolvePlaybookStatus(playbook) === 'archived';
}

function canLockPlaybook(playbook) {
  if (!playbook) return false;

  const resolvedStatus = resolvePlaybookStatus(playbook);

  if (resolvedStatus === 'archived') return false;
  if (resolvedStatus === 'locked') return false;
  if (resolvedStatus === 'reopened') return false;

  return true;
}

function canReopenPlaybook(playbook) {
  if (!playbook) return false;

  const resolvedStatus = resolvePlaybookStatus(playbook);

  if (resolvedStatus === 'archived') return false;
  if (resolvedStatus === 'reopened') return false;

  return resolvedStatus === 'locked';
}

function canArchivePlaybook(playbook) {
  if (!playbook) return false;

  const resolvedStatus = resolvePlaybookStatus(playbook);

  if (resolvedStatus === 'archived') return false;

  return resolvedStatus === 'locked';
}

function getAllowedLifecycleActions(playbook) {
  return {
    canLock: canLockPlaybook(playbook),
    canReopen: canReopenPlaybook(playbook),
    canArchive: canArchivePlaybook(playbook)
  };
}
function getClientConfigurationFoundation() {
  return {
    relationship: null,
    relationships: [],
    relationshipOptions: [
      {
        id: 'monthly_marketing_client',
        label: 'Monthly Marketing Client',
        recommendedWorkflowStepIds: [
          'configure_relationships',
          'configure_services',
          'collect_access',
          'enable_modules',
          'generate_workspace',
          'begin_onboarding'
        ]
      },
      {
        id: 'website_build_client',
        label: 'Website Build Client',
        recommendedWorkflowStepIds: [
          'configure_relationships',
          'configure_services',
          'collect_access',
          'enable_modules',
          'generate_workspace',
          'begin_onboarding'
        ]
      },
      {
        id: 'project_client',
        label: 'Project Client',
        recommendedWorkflowStepIds: [
          'configure_relationships',
          'configure_services',
          'collect_access',
          'enable_modules',
          'generate_workspace',
          'begin_onboarding'
        ]
      }
    ],
    services: [],
    serviceCatalog: [
      {
        id: 'monthly_marketing',
        label: 'Monthly Marketing',
        serviceOptionIds: [
          'catalogs',
          'email_marketing',
          'social_media'
        ],
        requiredAccessIds: [
          'facebook',
          'instagram',
          'constant_contact',
          'rain',
          'google_business_profile'
        ],
        workflowStepIds: [
          'configure_services',
          'collect_access',
          'enable_modules'
        ]
      },
      {
        id: 'website',
        label: 'Website',
        serviceOptionIds: [
          'theme',
          'hosting',
          'maintenance'
        ],
        requiredAccessIds: [
          'shopify',
          'google_business_profile'
        ],
        workflowStepIds: [
          'configure_services',
          'collect_access',
          'generate_workspace'
        ]
      },
      {
        id: 'design',
        label: 'Design',
        serviceOptionIds: [
          'branding',
          'logo',
          'ads'
        ],
        requiredAccessIds: [
          'facebook',
          'instagram'
        ],
        workflowStepIds: [
          'configure_services',
          'collect_access',
          'enable_modules'
        ]
      }
    ],
    serviceOptions: [],
    serviceOptionCatalog: [
      {
        id: 'catalogs',
        label: 'Catalogs'
      },
      {
        id: 'email_marketing',
        label: 'Email Marketing'
      },
      {
        id: 'social_media',
        label: 'Social Media'
      },
      {
        id: 'theme',
        label: 'Theme'
      },
      {
        id: 'hosting',
        label: 'Hosting'
      },
      {
        id: 'maintenance',
        label: 'Maintenance'
      },
      {
        id: 'branding',
        label: 'Branding'
      },
      {
        id: 'logo',
        label: 'Logo'
      },
      {
        id: 'ads',
        label: 'Ads'
      }
    ],
    requiredAccess: [],
    requiredAccessCatalog: [
      {
        id: 'shopify',
        label: 'Shopify'
      },
      {
        id: 'facebook',
        label: 'Facebook'
      },
      {
        id: 'instagram',
        label: 'Instagram'
      },
      {
        id: 'constant_contact',
        label: 'Constant Contact'
      },
      {
        id: 'rain',
        label: 'Rain'
      },
      {
        id: 'google_business_profile',
        label: 'Google Business Profile'
      }
    ],
    workflowSteps: [],
    workflowPreviewSteps: [
      {
        id: 'create_client',
        label: 'Create Client',
        alwaysShow: true
      },
      {
        id: 'configure_relationships',
        label: 'Configure Relationships',
        alwaysShow: true
      },
      {
        id: 'configure_services',
        label: 'Configure Services',
        alwaysShow: true
      },
      {
        id: 'collect_access',
        label: 'Collect Access',
        alwaysShow: true
      },
      {
        id: 'enable_modules',
        label: 'Enable Modules',
        alwaysShow: true
      },
      {
        id: 'generate_workspace',
        label: 'Generate Workspace',
        alwaysShow: true
      },
      {
        id: 'begin_onboarding',
        label: 'Begin Onboarding',
        alwaysShow: true
      }
    ],
    enabledModules: [],
    internalNotes: ''
  };
}

function getPlaybookPrimaryAction(playbook) {
  const resolvedStatus = resolvePlaybookStatus(playbook);
  const canEdit = isPlaybookEditable(playbook);

  return {
    label: canEdit ? 'Continue Playbook' : 'View Playbook',
    shortLabel: canEdit ? 'Continue' : 'View',
    href: playbook?.id
      ? `/pages/workspace-current-playbook?playbook=${playbook.id}`
      : '/pages/workspace-current-playbook',
    editable: canEdit,
    status: resolvedStatus
  };
}




    return {
    loadPlaybookById,
    getPlaybookById,
    getPlaybookDashboardById,
    getPlaybooksByClient,
    getPlaybookState,
    getCurrentPlaybook,
    getPlaybookWindow,
getNextPlaybookMonth,
getCurrentProductionMonth,
ensurePlaybookWindow,
createPlaybook,

    submitPlaybook,
    lockPlaybook,
    reopenPlaybook,
    requestPlaybookReopen,
    archivePlaybook,

    resolvePlaybookStatus,
    applyDeadlineLock,
    isCurrentPlaybook,
    enterReopenedState,

    canEditPlaybook,
    getLockReason,
    isPlaybookReopened,
    isPlaybookArchived,
    canLockPlaybook,
    canReopenPlaybook,
    canArchivePlaybook,
    getAllowedLifecycleActions,

    getClients,
    createClient,
    updateClient,
    archiveClient,
    getClientConfiguration,
    saveClientConfiguration,
    getClientAccount,
getClientTemplateConfiguration,
getMarketingPlanByClient,
    getServiceCatalog,
    upsertServiceCatalogItem,
    archiveServiceCatalogItem,
    saveClientMarketingPackage,
    getClientMarketingPackage,
    getPackageChangeRequests,
    submitPackageChangeRequest,
    getAccessPlatformCatalog,
    getClientAccessRequirements,
    saveClientAccessRequirements,
    markClientAccessProvided,
    getAccessSubmission,
    submitAccessDetails,
    revealAccessPassword,
    setAccessSubmissionStatus,
    getClientContracts,
    createContract,
    updateContract,
    archiveContract,
    deleteContractPermanently,
    uploadContractDocument,
    archiveContractDocument,
    deleteContractDocumentPermanently,
    listWaveBusinesses,
    getWaveConnection,
    setWaveBusiness,
    listWaveCustomers,
    getClientWaveCustomer,
    setClientWaveCustomer,
    getClientWaveInvoices,
    getMyWaveInvoices,
    getClientBrandAssets,
    uploadBrandAssetFile,
    createBrandAsset,
    archiveBrandAsset,
    getVendorLibrary,
    getClientVendorBrands,
    saveClientVendorBrands,
    saveField,
saveSection,
saveSectionCompletion,
savePlaybookSectionCount,
getPlaybookCompletion,
savePlaybook,

    formatPlaybookStatus,
    getPlaybookLifecycleState,
    getPlaybookPrimaryAction,
    getClientConfigurationFoundation,
    isPlaybookLocked,
    isPlaybookEditable
  };
})();